// /en/police-clearance/for/$country
import { policeCountrySectionsEn, policeHubSectionsEn } from "@/data/police-content";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import type { PoliceDeepEntry } from "@/data/police-deep-en";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { TARGET_COUNTRIES, getTargetCountry } from "@/data/police-target-countries";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import PoliceTripleKeyLinks from "@/components/PoliceTripleKeyLinks";
import ApostilleCrossLinks from "@/components/ApostilleCrossLinks";
import { Globe2, MessageCircle, Phone, BadgeCheck, AlertTriangle, ListChecks, BookOpen, ExternalLink } from "lucide-react";
import CountryDeepContent from "@/components/CountryDeepContent";
import { normalizeCountrySlug } from "@/lib/country-hague";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance/for/$country")({
  loader: async ({ params }) => {
    if (!getTargetCountry(params.country)) throw notFound();
    const { POLICE_DEEP_EN } = await import("@/data/police-deep-en");
    const deep: PoliceDeepEntry | null = POLICE_DEEP_EN[params.country] ?? null;
    return { deep, slug: params.country };
  },
  head: ({ params, loaderData }) => {
    const c = getTargetCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Not found", "/en/police-clearance/for/$country", "en")}] };
    const deep = loaderData?.deep;
    const url = `${SITE}/en/police-clearance/for/${c.slug}`;
    const title = deep?.metaTitle ?? `Thai Police Clearance for ${c.nameEn} | NYC`;
    const desc =
      deep?.metaDescription ??
      `Thai PCSC service for ${c.nameEn} visa applications — translation + ${c.hague ? "Apostille" : "MFA + Embassy Legalization"} in one package. Free consultation — message us today. Visas: ${c.visaTypes.slice(0, 3).join(", ")}.`;

    const baseGraph: { type: "application/ld+json"; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${url}#service`,
          name: `Thai Police Clearance for ${c.nameEn}`,
          serviceType: "Police Clearance for Visa Application",
          areaServed: [
            { "@type": "Country", name: "Thailand" },
            { "@type": "Country", name: c.nameEn },
          ],
          provider: {
            "@type": "LegalService",
            "@id": `${SITE}/#org`,
            name: "NYC Legal & Notary Service",
            telephone: "+66832494999",
            url: SITE,
          },
          offers: {
            "@type": "Offer",
            price: 4500,
            priceCurrency: "THB",
            availability: "https://schema.org/InStock",
          },
          aggregateRating: aggregateRatingNode(url),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "@id": `${url}#breadcrumb`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/en/police-clearance` },
            { "@type": "ListItem", position: 3, name: c.nameEn, item: url },
          ],
        }),
      },
    ];

    if (deep) {
      // Article schema for deep pages
      baseGraph.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${url}#article`,
          headline: deep.metaTitle,
          description: deep.metaDescription,
          inLanguage: "en",
          mainEntityOfPage: url,
          author: {
            "@type": "Organization",
            name: "NYC Legal & Notary Service",
            url: SITE,
          },
          publisher: {
            "@type": "Organization",
            "@id": `${SITE}/#org`,
            name: "NYC Legal & Notary Service",
            url: SITE,
          },
          datePublished: "2026-06-17",
          dateModified: "2026-06-17",
          about: { "@type": "Country", name: c.nameEn },
        }),
      });
      // FAQPage
      baseGraph.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          inLanguage: "en",
          mainEntity: deep.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
      // HowTo
      baseGraph.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": `${url}#howto`,
          name: `How to obtain a Thai Police Clearance for ${c.nameEn}`,
          inLanguage: "en",
          totalTime: "P25D",
          estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: 4500 },
          step: deep.pipeline.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.step,
            text: s.detail,
          })),
        }),
      });
    }

    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance/for/$country", "en") },
        { name: "description", content: ctaDesc(desc, "/en/police-clearance/for/$country", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance/for/$country", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance/for/$country", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { property: "og:type", content: deep ? "article" : "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance/for/${c.slug}`, `/en/police-clearance/for/${c.slug}`),
      ],
      scripts: [consolidateGraph([speakableSchema(), ...baseGraph])],
    };
  },
  component: PoliceCountryEnPage,
});

function PoliceCountryEnPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getTargetCountry(slug)!;
  const deep = (Route.useLoaderData() as { deep: PoliceDeepEntry | null }).deep;
  const sections = [...policeCountrySectionsEn(c), ...policeHubSectionsEn().slice(1, 3)];
  const related = TARGET_COUNTRIES.filter((x) => x.slug !== slug).slice(0, 9);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Globe2 className="h-3.5 w-3.5" /> For {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Thai Police Clearance for {c.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            PCSC + translation + {c.hague ? "Apostille (Hague Member)" : "MFA + Embassy Legalization"} — from THB 4,500, 15-25 business days.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {deep?.speakable ??
            `Thai Police Clearance (PCSC) for ${c.nameEn} — includes translation + ${c.hague ? "Apostille" : "Embassy Legalization"}. From THB 4,500, completed in 15-25 business days.`}
        </blockquote>

        {deep && (
          <>
            <section className="mt-10">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                {c.nameEn} Immigration Authority Requirements
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{deep.overview}</p>
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-[14px] text-slate-800">
                <strong className="text-amber-800">Validity window:</strong> {deep.acceptanceWindow}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <ListChecks className="h-6 w-6 text-amber-600" />
                End-to-End Pipeline ({c.nameEn})
              </h2>
              <ol className="mt-5 space-y-3">
                {deep.pipeline.map((s, i) => (
                  <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="font-bold text-navy-deep">{s.step}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{s.detail}</div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                Visa Matrix — When Is PCSC Required?
              </h2>
              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-slate-50 text-navy-deep">
                    <tr>
                      <th className="px-4 py-3">Visa / Application</th>
                      <th className="px-4 py-3">Required?</th>
                      <th className="px-4 py-3">Validity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {deep.visaMatrix.map((v, i) => (
                      <tr key={i} className="bg-white">
                        <td className="px-4 py-3 font-semibold text-slate-800">{v.visa}</td>
                        <td className="px-4 py-3 text-slate-700">{v.need}</td>
                        <td className="px-4 py-3 text-slate-600">{v.window}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
                Common Pitfalls We Eliminate
              </h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {deep.pitfalls.map((p, i) => (
                  <div key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                    <div className="font-bold text-rose-900">{p.title}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{p.detail}</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <div className="mt-12">
          <PoliceSections sections={sections} />
        </div>

        <section className="mt-12">
          <ContactCTA />
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={
                deep?.faqs ?? [
                  { q: `Is ${c.nameEn} a Hague member?`, a: c.hague ? "Yes — uses Thai Apostille." : `No — requires legalization at ${c.embassyTh}.` },
                  { q: `Which visas require this?`, a: c.visaTypes.join(", ") },
                  { q: `How long does it take?`, a: "15-25 business days for the full pipeline." },
                ]
              }
            />
          </div>
        </section>

        {deep && deep.citations.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              <BookOpen className="h-6 w-6 text-amber-600" />
              Official Sources & Citations
            </h2>
            <ul className="mt-5 space-y-2 text-[14px]">
              {deep.citations.map((cit, i) => (
                <li key={i}>
                  <a
                    href={cit.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-semibold text-amber-700 underline-offset-2 hover:underline"
                  >
                    {cit.label}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other Destination Countries</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/en/police-clearance/for/$country" params={{ country: r.slug }} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-slate-800">{r.nameEn}</span>
              </Link>
            ))}
          </div>
        </section>

        <PoliceTripleKeyLinks mode="en" countryNameTh={c.nameTh} countryNameEn={c.nameEn} />
        <ApostilleCrossLinks mode="en" source="police" sourceSlug={c.slug} countryLabel={c.nameEn} />
      </div>

      <CountryDeepContent
        lang="en"
        service="mfa"
        countryTh={c.nameTh}
        countryEn={c.nameEn}
        hague={c.hague}
        priceFromTh={4500}
        turnaround="15–25 business days"
        slug={normalizeCountrySlug(c.slug)}
      />

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    </main>
  );
}
